import { type Context, createContext, useContext } from 'react';

/**
 * Shared state for the profile-card reporting-line slide. Lifted into context so the trigger (on the
 * Manager row) and the sliding panes (in the card body) share one source of truth. `null` when the
 * card has no reporting-line data, in which case consumers render nothing.
 */
export interface ProfileCardSliderContextValue {
	/** Whether the reporting-line (back) pane is currently shown. */
	showBack: boolean;
	/** Reveal the reporting-line pane. */
	onView: () => void;
	/** Return to the profile (front) pane. */
	onBack: () => void;
	/** DOM id of the reporting-line region, for the trigger's `aria-controls`. */
	backPaneId: string;
	/** Increments each time the pane opens, so the list re-centers on the profiled user. */
	resetToken: number;
}

export const ProfileCardSliderContext: Context<ProfileCardSliderContextValue | null> =
	createContext<ProfileCardSliderContextValue | null>(null);

/**
 * Returns the profile-card slider controls, or `null` when the card has no reporting-line slider
 * (e.g. deactivated users or missing hierarchy data).
 */
export function useProfileCardSlider(): ProfileCardSliderContextValue | null {
	return useContext(ProfileCardSliderContext);
}
